/** Words and small helpers of the V1 pack boards (in the language on screen). */
import type { V1IconName } from '@/features/gate/v1/sprite'
import { cropWord, shortLot, tr } from '@/features/gate/v1/text'
import type { PackRunView } from '@/features/process/projection'
import type { KIconName } from './icons'

/** A pack icon name where a kit piece types it as the gate's names. */
export const ki = (name: KIconName) => name as V1IconName

export interface HeaderLot {
  /** "लाइन 1" / "लॉट …0031-K" */
  name: string
  /** "मूंगफली दाना" */
  line: string
}

/** The server's error code of a failed call (e.g. ALLERGEN_CLEAN_MISSING). */
export const errorCode = (e: unknown): string | undefined =>
  (e as { code?: unknown } | null)?.code && typeof (e as { code: unknown }).code === 'string'
    ? (e as { code: string }).code
    : undefined

/** The saving error in words (no network: the start and the dispatch need it). */
export function callFailed(e: unknown, fallback: string): string {
  const err = e as { kind?: string; code?: string; message?: string } | null
  if (err?.kind === 'network' || err?.kind === 'timeout') return tr('pack.v1.needsNet')
  const code = err?.code
  if (code) {
    const key = `pack.v1.error.${code}`
    const text = tr(key as Parameters<typeof tr>[0])
    if (text !== key) return text
  }
  return err?.message ? `${fallback} · ${err.message}` : fallback
}

/** The product in the language on screen ("मूंगफली दाना"; the product's own name otherwise). */
export function productName(r: Pick<PackRunView, 'sku' | 'crop_name'>): string {
  const hi = tr('pack.v1.lang') === 'hi'
  if (hi && typeof r.sku.name_hi === 'string' && r.sku.name_hi) return r.sku.name_hi
  if (/groundnut|peanut/i.test(`${r.sku.name} ${r.crop_name ?? ''}`)) return tr('pack.v1.groundnut')
  return hi ? cropWord(r.sku.name) : r.sku.name
}

/** "500 ग्राम" / "1 किलो". */
export const packSize = (netG: number) =>
  netG >= 1000 && netG % 1000 === 0
    ? tr('pack.v1.sizeKg', { kg: netG / 1000 })
    : tr('pack.v1.sizeG', { g: netG })

/** The header corner before the start: the line and the product. */
export const lineHeader = (r: PackRunView): HeaderLot => ({
  name: r.line_name ? tr('pack.v1.line', { line: r.line_name }) : tr('pack.v1.lineOne'),
  line: productName(r),
})

/** The header corner once the lot is fixed: the lot's tail and the product. */
export const lotHeader = (r: PackRunView, code: string): HeaderLot => ({
  name: tr('pack.v1.lotTail', { tail: shortLot(code) }),
  line: productName(r),
})
